import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from '@axios'

const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

export const usePreferredStatusStore = defineStore('preferredStatus', () => {
  const restaurantIds = ref([]) // direct restaurant-scope grants only
  const resolvedRestaurantIds = ref([]) // direct + group-resolved
  const scopeUserGroupIds = ref([])
  const groupNames = ref({}) // { [groupId]: groupName }
  const restaurantGroupMap = ref({}) // { [restaurantId]: groupId } for group-resolved restaurants
  const loadedAt = ref(null)
  const loading = ref(false)

  const isStale = computed(() => {
    if (!loadedAt.value) return true
    
    return Date.now() - loadedAt.value > CACHE_TTL_MS
  })

  async function loadPreferredStatus() {
    if (!isStale.value) return
    if (loading.value) return
    loading.value = true
    try {
      const { data } = await axios.get('/preferred-member/me')
      restaurantIds.value = data.restaurantIds || []
      resolvedRestaurantIds.value = data.resolvedRestaurantIds || []
      scopeUserGroupIds.value = data.scopeUserGroupIds || []
      groupNames.value = data.groupNames || {}
      restaurantGroupMap.value = data.restaurantGroupMap || {}
      loadedAt.value = Date.now()
    } catch {
      // non-fatal — member simply has no preferred status
      restaurantIds.value = []
      resolvedRestaurantIds.value = []
      scopeUserGroupIds.value = []
      groupNames.value = {}
      restaurantGroupMap.value = {}
    } finally {
      loading.value = false
    }
  }

  function isPreferredAt(restaurantId) {
    if (!restaurantId) return false
    
    return resolvedRestaurantIds.value.includes(restaurantId.toString())
  }

  function groupNameForRestaurant(restaurantId) {
    if (!restaurantId) return null
    const id = restaurantId.toString()
    if (!resolvedRestaurantIds.value.includes(id)) return null

    // Direct restaurant-scope grants are not attributed to any group
    if (restaurantIds.value.includes(id)) return null

    // Look up the specific group covering this restaurant
    const groupId = restaurantGroupMap.value[id]
    if (groupId && groupNames.value[groupId]) {
      return groupNames.value[groupId]
    }
    
    return null
  }

  function invalidate() {
    loadedAt.value = null
  }

  return {
    restaurantIds,
    resolvedRestaurantIds,
    scopeUserGroupIds,
    groupNames,
    restaurantGroupMap,
    loadedAt,
    loading,
    isStale,
    loadPreferredStatus,
    isPreferredAt,
    groupNameForRestaurant,
    invalidate,
  }
})
